/**
 * Content Folders Service
 * CRUD operations for content folder management
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { ContentFolder } from "../types/content.types";

/**
 * Get all folders (flat list — tree is built client-side)
 */
export async function getFolders(): Promise<ContentFolder[]> {
  const { data, error } = await supabase
    .from("content_folders")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching folders:", error);
    throw error;
  }

  return data || [];
}

/**
 * Create a new folder
 */
export async function createFolder(
  name: string,
  parentFolderId?: UUID | null,
): Promise<ContentFolder> {
  const { data, error } = await supabase
    .from("content_folders")
    .insert({
      name,
      parent_folder_id: parentFolderId || null,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating folder:", error);
    throw error;
  }

  return data;
}

/**
 * Rename a folder
 */
export async function renameFolder(
  id: UUID,
  name: string,
): Promise<ContentFolder> {
  const { data, error } = await supabase
    .from("content_folders")
    .update({ name, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error renaming folder:", error);
    throw error;
  }

  return data;
}

/**
 * Delete a folder (content items in it become unfoldered)
 */
export async function deleteFolder(id: UUID): Promise<void> {
  // Move all content items in this folder to unfoldered
  await supabase
    .from("content_items")
    .update({ folder_id: null })
    .eq("folder_id", id);

  // Move child folders up to parent (or root)
  const { data: folder } = await supabase
    .from("content_folders")
    .select("parent_folder_id")
    .eq("id", id)
    .single();

  await supabase
    .from("content_folders")
    .update({ parent_folder_id: folder?.parent_folder_id || null })
    .eq("parent_folder_id", id);

  const { error } = await supabase
    .from("content_folders")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting folder:", error);
    throw error;
  }
}

/**
 * Move a folder under a new parent (or null for root)
 */
export async function moveFolder(
  folderId: UUID,
  newParentId: UUID | null,
): Promise<void> {
  const { error } = await supabase
    .from("content_folders")
    .update({
      parent_folder_id: newParentId,
      updated_at: new Date().toISOString(),
    })
    .eq("id", folderId);

  if (error) {
    console.error("Error moving folder:", error);
    throw error;
  }
}

/**
 * Move a content item into a folder (or null to unfolder)
 */
export async function moveContentToFolder(
  contentItemId: UUID,
  folderId: UUID | null,
): Promise<void> {
  const { error } = await supabase
    .from("content_items")
    .update({ folder_id: folderId })
    .eq("id", contentItemId);

  if (error) {
    console.error("Error moving content to folder:", error);
    throw error;
  }
}
